<template>
  <validation-observer ref="form" slim>
    <div class="form-container">
      <b-row class="text-right" align="right">
        <b-col class="d-flex justify-content-end">
          <div class="mx-2">
            <b-form-group
              label="Status"
              label-size="sm"
              label-class="font-weight-bold"
            >
              <div
                :class="itemRowBackground(form.status)"
                class="px-2"
                size="sm"
                disabled
                style="width: fit-content;"
              >
                {{ form.status }}
              </div>
            </b-form-group>
          </div>
          <div
            v-if="form.bup_summary_id"
            class="summary border-left pl-3 pr-5"
          >
            <a :href="`/bup-summary/${form.bup_summary_id}/detail`">
              <b>See Summary BUP</b>
            </a>
          </div>
        </b-col>
      </b-row>
      <b-form>
        <div class="wrapper">
          <b-row>
            <b-col cols="6">
              <validation-provider
                name="Company"
                rules="required"
                v-slot="{ errors }"
                slim
                class="flex-fill"
              >
                <b-form-group
                  label="Company"
                  label-size="sm"
                  label-class="font-weight-bold"
                  :invalid-feedback="errors[0]"
                >
                  <b-form-select
                    ref="input"
                    autocapitalize="off"
                    v-model="form.company_id"
                    :options="companies"
                    value-field="id"
                    text-field="name"
                    :class="{ 'is-invalid': errors.length }"
                    :disabled="isView"
                  >
                    <template #first>
                      <b-form-select-option :value="null" disabled>Select Company Name</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="6">
              <validation-provider
                name="Contractor Name"
                rules="required"
                v-slot="{ errors }"
                slim
                class="flex-fill"
              >
                <b-form-group
                  label="Contractor Name"
                  label-size="sm"
                  label-class="font-weight-bold"
                  :invalid-feedback="errors[0]"
                >
                  <b-form-select
                    autocapitalize="off"
                    v-model="form.contractor_id"
                    :options="contractors"
                    value-field="id"
                    text-field="name"
                    @change="onContractorChange"
                    :class="{ 'is-invalid': errors.length }"
                    :disabled="isView"
                  >
                  <template #first>
                    <b-form-select-option :value="null" disabled>Select Contractor Name</b-form-select-option>
                  </template>
                  </b-form-select>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Contract Number"
                label-size="sm"
                label-class="font-weight-bold"
              >
                <b-form-input
                  autocapitalize="off"
                  :value="form.contract_number"
                  placeholder="Contract Number"
                  disabled
                />
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <validation-provider
                name="Period"
                rules="required"
                v-slot="{ errors }"
                slim
                class="flex-fill"
              >
                <b-form-group
                  label="Period"
                  label-size="sm"
                  label-class="font-weight-bold"
                  :invalid-feedback="errors[0]"
                >
                  <b-input-group>
                    <b-form-input
                      v-model="form.periode"
                      placeholder="MMM-YYYY"
                      autocomplete="off"
                      type="month"
                      :min="minPeriode()"
                      :max="maxPeriode()"
                      @change="onPeriodChange"
                      :disabled="isView ? isView : !form.contractor_id"
                    />
                  </b-input-group>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </div>
        <b-alert
          :show="form.periode && !fuelPrice"
          variant="danger"
        >
          There is no fuel price data for this period.
        </b-alert>

        <br>
        <!-- Deduction -->
        <b-card class="card-border-radius">
          <h6 class="font-weight-bold mb-4">Deduction</h6>
          <b-alert
            :show="!!error"
            variant="danger"
            dismissible
          >
            {{ error }}
          </b-alert>
          <deduction
            :form="form"
            :is-view="isView"
            :is-edit="isEdit"
            :other-rate="otherRate"
            :fuel-price="fuelPrice"
            :contractor="contractor"
            @update="updateOtherRate"
          />
        </b-card>

        <br>
        <!-- Additional -->
        <additional
          :form="form"
          :is-view="isView"
          :is-edit="isEdit"
          :fuel-price="fuelPrice"
          :contractor="contractor"
        />

        <br>
        <div class="d-flex mt-5">
          <b-button
            v-if="!isView"
            class="btn-reset"
            variant="outline-info"
            size="sm"
            @click="submit('Draft')"
          >
            Save Draft
          </b-button>
          <b-button
            class="btn-reset ml-auto mr-2"
            variant="outline-info"
            size="sm"
            @click="onClickBack"
          >
            Back
          </b-button>
          <b-button
            v-if="!isView"
            class="btn-custom"
            variant="info"
            size="sm"
            @click="submit('Submitted')"
          >
            Submit
          </b-button>
          <b-button
            v-if="canEdit && form.status != 'Submitted' && isView"
            class="btn-custom"
            variant="info"
            size="sm"
            @click="goToEdit"
          >
            Edit
          </b-button>
        </div>
        <modal-successful :message="text" />
      </b-form>
    </div>
  </validation-observer>

</template>

<script>
import api from '@/api'
import moment from 'moment'
import Deduction from './ComponentDeduction'
import Additional from './ComponentAdditional'
import ModalSuccessful from '@/partials/ModalSuccessful'
import { hasPermission } from '@/utils/permissions'
import { OTHER_EDIT, SUMMARY_VIEW } from '@/fixtures'

export default {
  components: {
    Deduction,
    Additional,
    ModalSuccessful
  },

  data: () => ({
    canEdit: hasPermission(OTHER_EDIT),
    canOpenSummary: hasPermission(SUMMARY_VIEW),
    otherRate: [],
    fuelPrice: 'null',
    contractors: [],
    companies: [],
    form: {
      company_id: null,
      contractor_id: null,
      periode: null,
      periode_month: null,
      periode_year: null,
      isInsentive: false,
      status: 'Draft',
      bup_others_installment: [],
      bup_others_explosive: [],
      bup_others_other_deduction: [],
      bup_others_additional: [],
      bup_others_insentive: [],
      attachmentInstallmentId: [],
      attachmentExplosiveId: [],
      attachmentOtherDeductionId: [],
      attachmentAdditionalId: []
    },
    text: '',
    isView: false,
    isEdit: false,
    error: null,
    contractor: {
      is_need_exchange: false,
      is_need_exchange_additional: false,
      rate: null,
      rate_additional: null
    }
  }),
  mounted() {
    this.fetchContractor()
    this.fetchCompany()
    if (!this.$route.path.includes('create')) {
      this.isView = this.$route.path.includes('detail')
      this.isEdit = this.$route.path.includes('edit')
      this.fetchDetailOther()
    }
  },
  computed: {},
  methods: {
    updateOtherRate(value) {
      this.otherRate = value
    },
    async fetchDetailOther() {
      const data = await api.other.get(this.$route.params.id)

      this.form = {
        ...this.form,
        ...data
      }

      this.contractor.is_need_exchange = this.form.contractor_is_need_exchange
      this.contractor.rate = this.form.contractor_rate
      if (data.periode_month.toString().length < 2) {
        data.periode_month = `0${data.periode_month}`
      }
      this.form.periode = `${data.periode_year}-${data.periode_month}`
      this.form.isInsentive = !!data.bup_others_insentive.length
      if (this.form.bup_others_explosive.length) {
        this.form.bup_others_explosive.map(v => {
          v.code = v.other_rates.code
          v.unit = v.other_rates.unit
        })
      }

      this.fetchOtherRate()
      this.fetchFuelPrice()
    },
    async fetchCompany() {
      const data = await api.company.list_active()
      this.companies = data
    },
    async fetchContractor() {
      try {
        const data = await api.contractor.list()
        this.contractors = data
      } catch (error) {}
    },
    async fetchOtherRate() {
      if (this.form.contractor_id) {
        this.otherRate = await api.contractor.otherRate(this.form.contractor_id)
        this.form.bup_others_explosive.map(v => {
          this.otherRate.map(i => {
            if (v.contractor_other_rate_id === i.id) {
              i.disabled = true
            }
          })
        })
      }
    },
    onPeriodChange() {
      this.otherRate.map(v => {
        v.disabled = false
      })
      this.form.bup_others_explosive = []
      this.fetchFuelPrice()
    },
    async fetchFuelPrice() {
      if (this.form.periode) {
        const date = new Date(this.form.periode)
        this.form.periode_month = date.getMonth() + 1
        this.form.periode_year = date.getFullYear()

        this.fuelPrice = await api.fuelPriceExchangeRate.fullPriceYearMonth(this.form.periode_year, this.form.periode_month)
      }
    },
    async submit(state) {
      try {
        if (state === 'Submitted') {
          const valid = await this.$refs.form.validate()
          if (!valid) {
            const errors = Object.values(this.$refs.form.errors)
            this.error = errors.find(v => v[0])[0]

            this.$nextTick(() => this.$refs.input.focus())
            return false
          }
        }

        const data = {
          periode_month: this.form.periode_month,
          periode_year: this.form.periode_year,
          contractor_id: this.form.contractor_id,
          company_id: this.form.company_id,
          is_insentive: this.form.isInsentive,
          state: state,
          bup_others_installment: this.form.bup_others_installment,
          bup_others_explosive: this.form.bup_others_explosive,
          bup_others_other_deduction: this.form.bup_others_other_deduction,
          bup_others_additional: this.form.bup_others_additional,
          bup_others_insentive: this.form.bup_others_insentive,
          attachment_id: this.form.attachmentInstallmentId
            .concat(
              this.form.attachmentExplosiveId,
              this.form.attachmentOtherDeductionId,
              this.form.attachmentAdditionalId
            )
        }

        this.isEdit ? await api.other.update(this.$route.params.id, data) : await api.other.submit(data)

        this.text = `${this.isEdit ? 'update' : 'save'} ${state === 'Submitted' ? '' : 'draft'} BUP Other`
        this.$bvModal.show('modal-successful')
        setTimeout(() => {
          this.onClickBack()
        }, 1500)
      } catch ({ response }) {
        this.$nextTick(() => {
          this.$bvToast.toast(`${response.status} - ${response.data.error.message}`, {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
      }
    },
    onContractorChange() {
      const number = this.contractors.find(v => v.id === this.form.contractor_id).contract_number

      this.contractor.is_need_exchange = this.contractors.find(v => v.id === this.form.contractor_id).is_need_exchange
      this.contractor.rate = this.contractors.find(v => v.id === this.form.contractor_id).rate
      this.contractor.is_need_exchange_additional = this.contractors.find(v => v.id === this.form.contractor_id).is_need_exchange_additional
      this.contractor.rate_additional = this.contractors.find(v => v.id === this.form.contractor_id).rate_additional

      this.$set(this.form, 'contract_number', number)
      this.form.periode = null
      this.form.bup_others_explosive = []
      this.form.bup_others_insentive = []
      this.form.isInsentive = false
      this.fetchOtherRate()
    },
    minPeriode() {
      if (this.form.contractor_id) {
        const contractorPeriode = this.contractors.length
          ? this.contractors.find(v => v.id === this.form.contractor_id).contract_periode_start
          : null
        const date = new Date(contractorPeriode)
        return moment(date).format('YYYY-MM')
      }
      return ''
    },
    maxPeriode() {
      if (this.form.contractor_id) {
        const contractorPeriode = this.contractors.length
          ? this.contractors.find(v => v.id === this.form.contractor_id).contract_periode_end
          : null
        const date = new Date(contractorPeriode)
        return moment(date).format('YYYY-MM')
      }
      return ''
    },
    goToEdit() {
      this.$router.push({ path: `/bup-other/${this.$route.params.id}/edit` })
      this.isView = false
      this.isEdit = true
    },
    onClickBack() {
      this.$router.push({ path: '/bup-other' })
    },
    itemRowBackground(status) {
      if (status === 'Draft') {
        return 'draft'
      } else if (status === 'Submitted') {
        return 'approved'
      } else if (status === 'Revise') {
        return 'revised'
      } else if (status === 'Waiting Approval') {
        return 'waiting-approval'
      } else {
        return 'waiting-approval'
      }
    }
  }
}
</script>

<style lang="scss">
.card-border-radius {
  border-radius: 16px;
}

.form-control, .custom-select {
  font-size: 12px;
}

.col-form-label,
.col-form-label-sm {
  font-size: 12px !important;
}

.check-periode {
  font-size: 12px
}

.total {
  font-size: 16px;
}

.supporting-file p {
  font-size: 12px;
}

.background-file {
  background-color: #DBF9F6;
  border-radius: 16px;
}

.size-file {
  color: #D0C9D6;
}

.dropbox {
  border: 2px dashed #463191;
  padding: 40px 10px;
  min-height: 200px;
  position: relative;
  cursor: pointer;
  border-radius: 16px;
  text-align: center;
}

.input-file {
  margin-top: -40px;
  margin-left: -555px;
  opacity: 0;
  width: 100%;
  height: 280px;
  position: absolute;
  cursor: pointer;
}

.dropbox p {
  font-size: 12px;
  text-align: center;
  padding-top: 20px;
}

#text-dropbox {
  font-size: 16px;
  text-align: center;
}

.text-ellipsis {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropbox-files {
  border: 2px dashed #463191;
  padding: 10px 10px;
  min-height: 150px;
  position: relative;
  border-radius: 16px;
  text-align: center;
}

.card-list {
  display: grid;
  grid-gap: 2px;
}

.color-date {
  color: #737AF1;
}

.pay-background {
  width: 100%;
  height: 100%;
  background-color: #e9fbf9;
}

.pending-background {
  width: 100%;
  height: 100%;
  background-color: #EAE7F3;
}

.text-mid {
  padding-top: 13px;
}

.summary {
  color: #737AF1;
  font-size: 12px;
  height: 30px;
  text-align: center;
  margin-top: 23px;
}
</style>
